import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, Building2, Check, Pencil } from "lucide-react";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { COURT_TYPE_LABELS, branchDisplay } from "../utils/workspace";

const ALL = "__all__";
const NONE = "__none__";
const KEY = (user) => `themis_stats_scope_${user?.id || "anon"}`;

/** Αποθηκευμένο πεδίο Στατιστικών: {branch, court_type} (user Νοέ 2026: ΜΟΝΟ Κατάστημα + Τύπος). */
export const readStatsScope = (user) => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY(user)) || "null");
    return v && v.confirmed_at && v.year === undefined ? v : null;
  } catch { return null; }
};
export const saveStatsScope = (user, scope) => {
  const v = { branch: scope.branch || "", court_type: scope.court_type || "", confirmed_at: new Date().toISOString() };
  localStorage.setItem(KEY(user), JSON.stringify(v));
  return v;
};

export const statsScopeLabel = (s) => {
  if (!s) return "";
  const b = s.branch ? branchDisplay(s.branch) : "Όλα τα Καταστήματα";
  const ct = COURT_TYPE_LABELS[s.court_type || ""] || s.court_type;
  return `${b} / ${ct}`;
};

/** Πύλη Στατιστικών: Κατάστημα + Τύπος Δικαστηρίου (υποχρεωτικά) πριν τη φόρτωση. */
export const StatsScopeGate = ({ user, initial, onConfirm, onCancel }) => {
  const isAdmin = user?.role === "admin";
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(initial?.confirmed_at ? (initial.branch || ALL) : (user?.court_branch || NONE));
  const [courtType, setCourtType] = useState(initial?.confirmed_at ? (initial.court_type || ALL) : NONE);

  useEffect(() => {
    axios.get(`${API}/branches`).then((r) => {
      const list = r.data?.branches || [];
      setBranches(list);
      if (!initial?.confirmed_at) {
        const mine = list.find((b) => user?.court_branch && (user.court_branch.includes(b.label) || b.label.includes(user.court_branch)));
        if (mine) setBranch(mine.label); else if (list.length === 1) setBranch(list[0].label);
        if (userCourtTypes.length === 1) setCourtType(userCourtTypes[0]);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const courtOptions = isAdmin ? ["Μονομελές", "Πολυμελές"] : userCourtTypes;
  const ready = branch !== NONE && courtType !== NONE;

  return (
    <Card className="border-indigo-200 bg-indigo-50/40" data-testid="stats-scope-gate">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="w-5 h-5 text-indigo-700" />Επιλογή Καταστήματος &amp; Τύπου Δικαστηρίου</CardTitle>
        <CardDescription>Τα στατιστικά φορτώνουν μετά την επιλογή. Έτος και Αντικείμενο επιλέγονται στα φίλτρα της σελίδας.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" />Κατάστημα *</Label>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="w-72 h-9 bg-white" data-testid="stats-scope-branch"><SelectValue placeholder="Επιλέξτε κατάστημα" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} disabled>Επιλέξτε κατάστημα</SelectItem>
              {isAdmin && <SelectItem value={ALL} data-testid="stats-scope-branch-all">Όλα τα Καταστήματα</SelectItem>}
              {branches.map((b) => <SelectItem key={b.label} value={b.label} data-testid={`stats-scope-branch-${b.label}`}>{b.display || branchDisplay(b.label, b.is_regional)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Τύπος Δικαστηρίου *</Label>
          <Select value={courtType} onValueChange={setCourtType}>
            <SelectTrigger className="w-56 h-9 bg-white" data-testid="stats-scope-court-type"><SelectValue placeholder="Επιλέξτε τύπο" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE} disabled>Επιλέξτε τύπο</SelectItem>
              {(isAdmin || courtOptions.length > 1) && <SelectItem value={ALL} data-testid="stats-scope-court-type-all">Όλοι οι τύποι</SelectItem>}
              {courtOptions.map((ct) => <SelectItem key={ct} value={ct} data-testid={`stats-scope-court-type-${ct}`}>{ct}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => onConfirm({ branch: branch === ALL ? "" : branch, court_type: courtType === ALL ? "" : courtType })}
                className="h-9 bg-indigo-700 hover:bg-indigo-800" disabled={!ready} data-testid="stats-scope-confirm-btn">
          <Check className="w-4 h-4 mr-1" />Επιβεβαίωση &amp; Φόρτωση
        </Button>
        {onCancel && <Button variant="ghost" className="h-9" onClick={onCancel} data-testid="stats-scope-cancel-btn">Άκυρο</Button>}
        {!ready && <span className="text-xs text-slate-500" data-testid="stats-scope-required-hint">Επιλέξτε Κατάστημα και Τύπο για να συνεχίσετε.</span>}
      </CardContent>
    </Card>
  );
};

export const StatsScopeBadge = ({ scope, onChange }) => (
  <div className="flex items-center gap-2 text-xs bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5" data-testid="stats-scope-badge">
    <BarChart3 className="w-3.5 h-3.5 text-indigo-600" />
    <span className="font-medium text-slate-700" data-testid="stats-scope-label">{statsScopeLabel(scope)}</span>
    <button type="button" onClick={onChange} className="text-indigo-700 hover:underline flex items-center gap-1" data-testid="stats-scope-change-btn">
      <Pencil className="w-3 h-3" />Αλλαγή
    </button>
  </div>
);
